import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  BookMarked,
  Loader2,
  Pin,
  PinOff,
  Trash2,
  Save,
  Copy,
  ArrowLeft,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import {
  listMyCitations,
  getMyCitation,
  updateMyCitation,
  deleteMyCitation,
} from "@/lib/api/ai-writing.functions";

export const Route = createFileRoute("/_authenticated/portal/my-citations")({
  head: () => ({
    meta: [{ title: "My Citations | Nkadimeng Danny" }],
  }),
  component: MyCitationsPage,
});

type Row = {
  id: string;
  title: string;
  kind: string;
  style: string;
  source_type: string | null;
  output_text: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
};

const KIND_LABELS: Record<string, string> = {
  bibliography: "Bibliography",
  reference_list: "Reference list",
  footnote_list: "Footnote list",
  single_citation: "Single citation",
  footnotes_passage: "Footnoted passage",
};

function MyCitationsPage() {
  const runList = useServerFn(listMyCitations);
  const runGet = useServerFn(getMyCitation);
  const runUpdate = useServerFn(updateMyCitation);
  const runDelete = useServerFn(deleteMyCitation);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ title: string; output_text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await runList();
      setRows((r.citations as Row[]) ?? []);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function openOne(id: string) {
    setActiveId(id);
    setEditing(null);
    try {
      const r = await runGet({ data: { id } });
      setEditing({ title: r.citation.title, output_text: r.citation.output_text });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not load citation.");
    }
  }

  async function saveActive() {
    if (!activeId || !editing) return;
    setSaving(true);
    try {
      await runUpdate({
        data: { id: activeId, title: editing.title, outputText: editing.output_text },
      });
      toast.success("Saved.");
      await refresh();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  async function togglePin(row: Row) {
    try {
      await runUpdate({ data: { id: row.id, pinned: !row.pinned } });
      await refresh();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not pin.");
    }
  }

  async function removeOne(row: Row) {
    if (!confirm(`Delete "${row.title}"?`)) return;
    try {
      await runDelete({ data: { id: row.id } });
      if (activeId === row.id) {
        setActiveId(null);
        setEditing(null);
      }
      await refresh();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not delete.");
    }
  }

  const visible = rows.filter((r) =>
    !filter
      ? true
      : (r.title + " " + r.output_text + " " + r.kind + " " + r.style)
          .toLowerCase()
          .includes(filter.toLowerCase()),
  );

  return (
    <div className="container py-8">
      <Link to="/education/writing" className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy">
        <ArrowLeft className="h-3.5 w-3.5" /> Writing Academy
      </Link>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-navy/5 p-2 text-navy"><BookMarked className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl text-navy">My Citations</h1>
            <p className="text-xs text-muted-foreground">Every bibliography, reference list and footnote you've generated — edit, pin or re-export.</p>
          </div>
        </div>
        <Link
          to="/education/writing/bibliography"
          className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-2 text-xs font-semibold text-navy-foreground"
        >
          <Plus className="h-3.5 w-3.5" /> New
        </Link>
      </div>

      <input
        type="search"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by title, style or content…"
        className="mt-4 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="rounded-lg border border-input bg-card">
          {loading ? (
            <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : visible.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No saved citations yet. Generate one from the{" "}
              <Link to="/education/writing/bibliography" className="underline">Bibliography Generator</Link>.
            </p>
          ) : (
            <ul className="max-h-[70vh] divide-y divide-border overflow-auto">
              {visible.map((r) => (
                <li
                  key={r.id}
                  className={`flex items-start gap-2 p-3 text-left text-sm hover:bg-muted/40 ${
                    activeId === r.id ? "bg-muted/60" : ""
                  }`}
                >
                  <button
                    onClick={() => openOne(r.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="truncate font-semibold text-navy">
                      {r.pinned && <Pin className="mr-1 inline h-3 w-3 text-amber-600" />}
                      {r.title}
                    </p>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {KIND_LABELS[r.kind] ?? r.kind} · {r.style}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Updated {new Date(r.updated_at).toLocaleDateString()}
                    </p>
                  </button>
                  <div className="flex shrink-0 flex-col gap-1">
                    <button
                      title={r.pinned ? "Unpin" : "Pin"}
                      onClick={() => togglePin(r)}
                      className="rounded p-1 text-muted-foreground hover:bg-background hover:text-navy"
                    >
                      {r.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      title="Delete"
                      onClick={() => removeOne(r)}
                      className="rounded p-1 text-muted-foreground hover:bg-rose-50 hover:text-rose-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-input bg-card p-4">
          {!activeId ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Select a saved citation to view or edit.
            </p>
          ) : !editing ? (
            <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-3">
              <input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-semibold"
              />
              <textarea
                value={editing.output_text}
                onChange={(e) => setEditing({ ...editing, output_text: e.target.value })}
                rows={20}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={saveActive}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-md bg-navy px-4 py-2 text-xs font-semibold text-navy-foreground disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save changes
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(editing.output_text);
                    toast.success("Copied.");
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-2 text-xs hover:bg-muted"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
